/**
 * Module 4: check the draft (3 minutes). The draft table in the middle of the page: one
 * row per line with an icon and a word (matched, confirm, pick, no item), the three
 * groups (Needs you, Matched, Nothing to quote), the icon legend, the row button, the
 * chevron on the status, the big button, wrong quantities, guessed items, phantom lines.
 *
 * Lines match practice/facts.ts PRACTICE-0001: line 1 blade (matched), line 2 bolt
 * M12 x 35 read as 35 instead of 200 (pick), line 3 liner plate (confirm, Item found by
 * its number, lead time not known). Round 3 (12 Oct 2026): the example row figure on l1
 * and the status legend figure on l1-icons (drawn from LEGEND of lib/line-icons.ts by
 * the figure, so the help, the cheat sheet and this module say the same ten lines);
 * every sentence in Simplified Technical English. The phantom wear plate of l2 and l7 is
 * a described example, not a line of PRACTICE-0001 (which has no phantom line), so the
 * text says so instead of pointing at a row the stage does not show.
 */

import type { Module } from "@/features/training/types";

import { checkSlide } from "../check-slide";

export const MODULE: Module = {
  id: "lines",
  title: "Check the draft",
  menuTitle: "Draft",
  minutes: 3,
  kind: "lesson",
  goals: [
    "Read the icon and the word of each row: matched, confirm, pick or no item.",
    "Work the groups in order: Needs you first, then Matched, then Nothing to quote.",
    "Find a wrong quantity, a phantom line or a guessed item.",
    "Correct a row with Confirm, Pick item, Correct or No item.",
  ],
  helpTopics: ["case-page", "i2q-decided-lines"],
  slides: [
    {
      id: "l1",
      tone: "light",
      title: "When you need to check what the customer asked for",
      job: "When you need to check what the customer asked for, read the draft table under the mail card.",
      path: ["Case", "Draft"],
      body: [
        {
          kind: "text",
          text: "Step two, **Check the draft**. The draft is a table in the middle of the page. Each row is one line of the inquiry. `Status` shows an icon and a word. `Customer asked` shows the customer's words, with an icon for their source. `I2Q proposes` shows the item number and its name, with an icon for how I2Q found it. `Qty`, `Price` with its band icon and `Lead` with its icon carry the figures. The row button sits in the row, at the right end:",
        },
        { kind: "figure", figure: "table-row" },
        {
          kind: "list",
          items: [
            "`matched`, green check: I2Q is sure. Compare the row with the mail, then accept the matched rows with the big button.",
            "`confirm`, blue question mark: a good guess. Read the row. Then press `Confirm` in the row.",
            "`pick`, orange box with a magnifier: no sure item. Press `Pick item` in the row. Pick one from the list.",
            "`no item`, grey ban sign: somebody said there is no item for this line. Tell the customer.",
          ],
        },
        {
          kind: "text",
          text: "The rows come in three groups, in this order: `Needs you` (pick and confirm), `Matched` (I2Q is sure or you decided) and `Nothing to quote` (no item, folded). The lines that need you are on top. The strip above the table says how many rows have an item, for example 1 of 3 found. It also shows the count per group, with its icon.",
        },
        {
          kind: "text",
          text: "Click the customer's words in a row and the mail card marks them. You see the context without scrolling. Click the status word and the row opens its details. Open the details only when the word is not matched or a price looks wrong.",
        },
        {
          kind: "text",
          text: "`Show details` shows the long figures in the cells on a laptop; on a phone or in Outlook it opens every row at once. The page remembers your setting.",
        },
        {
          kind: "text",
          text: "The big button in the bar at the bottom names the next step. It says `Pick 2 items` while a row has no item. It says `Confirm 2 items` when every open row is a suggestion: one click confirms them all. It says `Accept 1 matched item` when only matched rows are left. Shortcut {key:a}.",
        },
        {
          kind: "text",
          text: "The sentence next to the big button says what stands between the case and a reply. Click it and the page goes to the control. `Open points` lists everything that is open.",
        },
        {
          kind: "text",
          text: "A customer can order on our quotation or send it back signed. Then each row is a position of that quotation. The row details say `From our quotation` with its number and the change the mail asks for. Every row is a suggestion: check the rows against the order.",
        },
        {
          kind: "text",
          text: "I2Q can read a mail again. A decision stays on its row when I2Q finds the row again. A decision without a row stays under `Details`, in `Earlier decisions`. Then decide the row that I2Q read.",
        },
        {
          kind: "text",
          text: "The practice under this slide shows the rows as cards, the form of a phone. The table and the row menu (the three dots) are on the real case page. The real page also has the quantity in the cell and the list `Open points`.",
        },
      ],
      interaction: {
        id: "l1-table",
        kind: "hotspots",
        required: true,
        minOpen: 2,
        stage: { view: "case", caseId: "PRACTICE-0001", part: "lines" },
        spots: [
          {
            anchor: "case-lines",
            label: "The draft",
            text: "One row per line, the lines that need you on top. Read the icon and the word first. Then read the customer's words and what I2Q proposes.",
          },
          {
            anchor: "case-action",
            label: "The one action",
            text: "The big button says what the draft still needs. It changes as you work through the rows.",
          },
        ],
      },
      check: "Read the customer's words for every row, also for the matched ones.",
      labels: [
        "matched",
        "confirm",
        "pick",
        "no item",
        "Needs you",
        "Matched",
        "Nothing to quote",
        "Show details",
        "Pick item",
        "Status",
        "Price",
        "Qty",
        "Open points",
      ],
    },
    {
      id: "l1-icons",
      tone: "grey",
      title: "What the icons mean",
      job: "Read the legend once. The same icons sit in the table, in the strip above it and in the queue.",
      path: ["Case", "Draft", "Status"],
      body: [
        {
          kind: "text",
          text: "Every icon has one meaning everywhere. A word or a label sits next to it. You never guess from the colour alone.",
        },
        { kind: "figure", figure: "status-legend" },
        {
          kind: "text",
          text: "A person with a check on a price means: you or a colleague set it. Set price, Change price and Undo price are in the row menu (the three dots). The practice has no row menu. Try it on a real case.",
        },
      ],
      check:
        "Start with the orange rows (pick). Then do the blue rows (confirm). The green rows (matched) are accepted with the big button.",
      labels: ["matched", "confirm", "pick", "no item", "Confirm", "Pick item"],
    },
    {
      id: "l2",
      tone: "grey",
      title: "Find the wrong rows",
      job: "Find the 3 rows that are wrong.",
      body: [
        {
          kind: "text",
          text: "I2Q can read a size as a quantity. It can guess an item. It can copy a line from an old quotation. Find these mistakes before the customer does.",
        },
        {
          kind: "text",
          text: "The list under the draft describes the rows in words. One row is an example from another case: the draft above does not show it.",
        },
      ],
      interaction: {
        id: "l2-spot",
        kind: "spot",
        required: true,
        mustFind: 3,
        prompt: "Click each row that is wrong.",
        stage: { view: "case", caseId: "PRACTICE-0001", part: "lines" },
        rows: [
          {
            id: "l2-bolt",
            text: "Bolt M12 x 35, 200 pcs. The row says 35 pcs.",
            issue: true,
            why: "35 is the length of the bolt. The customer wants 200 pcs. Open the details and press Correct next to the quantity.",
          },
          {
            id: "l2-liner",
            text: "Liner plate as last time. The row says 099-200-07, confirm, Item found by its number.",
            issue: true,
            why: "The mail has no number. Item found by its number is a guess here. Check the last quotation to this customer before you click Confirm.",
          },
          {
            id: "l2-phantom",
            text: "An example row from another case: Wear plate, 4 pcs. The mail of that case does not mention a wear plate.",
            issue: true,
            why: "This line came from the quotation history, not from the mail. Open its details and click `No item`.",
          },
          {
            id: "l2-blade",
            text: "Blade, blast wheel 099-100-01, 8 pcs. The row says matched.",
            issue: false,
            why: "The item number and the quantity match the mail. This row is right.",
          },
          {
            id: "l2-unit",
            text: "Unit pcs on the blade row. The mail says 8 pcs.",
            issue: false,
            why: "The unit matches the mail. This row is right.",
          },
        ],
      },
      check:
        "Compare the quantity, the unit and the item with the mail. A size inside the text is not a quantity.",
      labels: ["Item found by its number", "No item", "Correct"],
    },
    {
      id: "l3",
      tone: "light",
      title: "Correct the rows, then press the one action",
      job: "Correct the bolt quantity, decide the open rows, then accept the matched row.",
      path: ["Case", "Draft", "Status", "Correct"],
      body: [
        {
          kind: "list",
          items: [
            "`Confirm`: the proposed item is right. At the right end of a row that says confirm.",
            "The big button confirms every suggested row in one click. Check each item first: a confirmed item goes into the reply.",
            "`Pick item`: pick the item from the list. At the right end of a row that says pick.",
            "`The item name differs from the line`: the name of this item shares no word with the line. Check it before you pick it.",
            "The quantity: click it in the `Qty` cell, type a whole number and press Enter. Tab goes to the next row.",
            "`Set quantity for all lines`: in the strip above the table. It sets one quantity on many rows.",
            "`Quantity not given, 1 used`: the mail gave no quantity, so I2Q used 1. An asterisk marks the 1. The reply asks the customer to confirm the quantity. Type the real quantity when you know it.",
            "`Correct`: behind the chevron on the status word, next to the quantity. Use it when I2Q read a wrong quantity or unit.",
            "`No item`: behind the chevron on the status word. Use it when the line needs no item, or when the line is wrong.",
            "`Change item`: in the row menu (the three dots) of a matched or confirm row. I2Q is sure, but the customer meant another item. A matched row nobody decided also has it behind the chevron.",
            "`Undo`: behind the chevron on the status word. Take your decision back.",
          ],
        },
      ],
      interaction: {
        id: "l3-fix",
        kind: "practice",
        required: true,
        stage: { view: "case", caseId: "PRACTICE-0001", part: "lines" },
        task: "accept-matched-and-fix-quantity",
        steps: [
          "The bolt row sits on top under Needs you. Open its details with the chevron on the status word. Click `Correct` next to the quantity. Type 200 and save.",
          "On the bolt row, click `Pick item` and pick 099-300-12. The item was right. Only the quantity was wrong.",
          "On the liner plate row, click `Confirm`. The mail says as last time. This customer got 099-200-07 last time.",
          "The big button reads `Accept 1 matched item`. Click it.",
        ],
        success:
          "All three rows are decided: the blade accepted, the bolt at 200 pcs, the liner confirmed. Quote 200 pcs in F&O and in your reply.",
        hints: [
          "The chevron sits on the status word of each row. `Correct`, next to the quantity, is behind it.",
          "The big button says `Pick 2 items` until you decide the open rows. Then it says `Accept 1 matched item`.",
        ],
      },
      check: "After you accept, read each row once more. Accept is fast, the check is yours.",
      labels: [
        "Confirm",
        "Pick item",
        "No item",
        "Undo",
        "Correct",
        "Change item",
        "Qty",
        "Set quantity for all lines",
        "Quantity not given, 1 used",
      ],
    },
    {
      id: "l4",
      tone: "grey",
      title: "Read the details of a row",
      job: "Sort each reason: is it enough, or do you compare with the mail?",
      body: [
        {
          kind: "text",
          text: "The icon in `I2Q proposes` says how I2Q found the item. A hash means the exact number. A link means an alias or a successor. A clock on a page means the history. Sparkles mean a similar description. A person with a check means you or a colleague picked it.",
        },
        {
          kind: "text",
          text: "The chevron on the status word opens the details: why I2Q picked the item, the other candidates, the price and the earlier quotations. I2Q is strong on exact item numbers and on replaced items. Item found by its number can still be a guess. Look at what the customer wrote.",
        },
      ],
      interaction: {
        id: "l4-sort",
        kind: "sort",
        required: true,
        prompt: "Which reasons are enough on their own?",
        bins: ["Enough evidence", "Compare with the mail"],
        cards: [
          {
            id: "l4-exact",
            text: "Exact item number match",
            bin: 0,
            why: "The customer wrote the item number and it matches. This is strong evidence.",
          },
          {
            id: "l4-super",
            text: "A successor replaced the item",
            bin: 0,
            why: "I2Q follows the replacement in the item master. Quote the successor.",
          },
          {
            id: "l4-model",
            text: "Item found by its number, on a machine model",
            bin: 1,
            why: "A model number is not an item number. Check what the customer asked for.",
          },
          {
            id: "l4-none",
            text: "No number in the mail",
            bin: 1,
            why: "I2Q guessed from words only. Compare with the mail or the last quotation.",
          },
        ],
      },
      check:
        "Item found by its number is enough only after you compare it with the customer's words.",
      labels: ["Item found by its number", "I2Q proposes"],
    },
    {
      id: "l5",
      tone: "light",
      title: "Lead time, and a check that holds the case",
      job: "When you need to find a lead time or the case says Check, read the row and the sheet.",
      body: [
        {
          kind: "text",
          text: "The `Qty`, `Price` and `Lead` columns carry the figures. A check badge on the price means in band. In the `Price` column, a warning triangle on a price means below margin, and a check holds the case. A dashed circle means no price. The cell says why, for example `No price in F&O`.",
        },
        {
          kind: "text",
          text: "A clock on the lead time means F&O holds it. A clock with an arrow means it comes from an earlier quotation. Confirm it first. A dashed circle means F&O holds none. An hourglass means not looked up yet. The row then needs a lead time from F&O, or the reply says the lead time will follow. An alert circle on the quantity means the quantity needs a look. An asterisk on the quantity means the mail gave none, so I2Q used 1.",
        },
        {
          kind: "text",
          text: "A lead time can also come from the item master, when IT has switched that on for your area. The cell then names the source, for example `Item master: sales lead time`. The reply says about that many days, to be confirmed.",
        },
        {
          kind: "text",
          text: "When a rule holds the case, the big button says `Check` with the reason. The sentence next to it says what to do. The button goes to the held row. For a rule without a row it opens the `Details` sheet at the checks. The sheet also holds the quotation, the order system, the documents, the related mails and the history.",
        },
      ],
      interaction: {
        id: "l5-check",
        kind: "check",
        required: true,
        question: {
          id: "l5-q",
          topic: "lines",
          stem: "The mail says M12 x 35 bolts, 200 pcs. The row says 35 pcs. What do you do?",
          options: [
            {
              id: "l5-q-a",
              text: "Press the big button. The bolt is right.",
              correct: false,
              fb: "No. The item may be right, but 35 is the length. The customer wants 200 pcs.",
            },
            {
              id: "l5-q-b",
              text: "Give feedback and leave the row.",
              correct: false,
              fb: "No. Feedback tells the build team. It does not change the row.",
            },
            {
              id: "l5-q-c",
              text: "Open the row with the chevron on its status word. Click `Correct` next to the quantity. Type 200.",
              correct: true,
              fb: "Right. The correction stays on the case and goes into the reply.",
            },
            {
              id: "l5-q-d",
              text: "Use `Pick item` and pick a bolt with 200 in its name.",
              correct: false,
              fb: "No. The item is right. Only the quantity is wrong.",
            },
          ],
        },
      },
      check: "A missing lead time goes into the reply as an open point. Do not leave it out.",
      labels: ["Check", "Details", "Correct", "Item master: sales lead time"],
    },
    checkSlide({
      id: "l6",
      tone: "grey",
      title: "Check: a row that says pick",
      topic: "lines",
      stem: "A row says `pick`. What do you do?",
      answers: [
        [
          "Press the big button with the other rows.",
          false,
          "No. The big button says Pick 1 item while this row waits. It accepts nothing.",
        ],
        [
          "Click `Pick item` in the row and pick from the list.",
          true,
          "Right. You decide this row.",
        ],
        ["Delete the case.", false, "No. One open row does not make the case wrong."],
        ["Wait for I2Q to try again.", false, "No. I2Q does not come back to it on its own."],
      ],
      check: "pick means: I2Q has no sure item. You pick.",
      labels: ["pick", "Pick item"],
    }),
    checkSlide({
      id: "l7",
      tone: "light",
      title: "Check: a line nobody asked for",
      topic: "lines",
      stem: "A draft has a wear plate row. The mail of that case does not mention a wear plate. What is it?",
      answers: [
        ["A bonus line. Leave it in.", false, "No. Quote only what the customer asked for."],
        [
          "A line nobody asked for, often copied from the quotation history. Open it with the chevron on its status word. Click `No item`.",
          true,
          "Right. Compare the rows with the mail, one by one.",
        ],
        ["A rule from your country.", false, "No. Rules do not add lines to a case."],
        ["A line from another customer.", false, "No. It came from the history of this customer."],
      ],
      check: "Count the lines in the mail and the rows in the draft. They must match.",
      labels: ["No item"],
    }),
    checkSlide({
      id: "l8",
      tone: "grey",
      title: "Check: a warning triangle on a price",
      topic: "prices",
      stem: "A row in the Matched group shows a warning triangle next to its price. What does it mean?",
      answers: [
        [
          "The price is missing.",
          false,
          "No. A missing price shows a dashed circle and the reason, for example No price in F&O.",
        ],
        [
          "The price is below margin. A check holds the case until a manager approves it.",
          true,
          "Right. The big button says Check with the reason. Your manager presses Approve price: on the big button, in the row menu of the line, or under More.",
        ],
        [
          "The lead time is not looked up yet.",
          false,
          "No. That is the hourglass on the lead time.",
        ],
        ["The item is wrong.", false, "No. The triangle is about the price band, not the item."],
      ],
      check: "A warning triangle on a price means below margin. The row is still matched.",
      labels: ["Check", "Approve price", "No price in F&O"],
    }),
  ],
};
